import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as d3 from 'd3'; // Add D3 import
import { useApi } from '../../api/api';
import './ParameterTracking.css';
// import PaperRankingToolbar from './PaperRankingToolbar'; // Import the ranking toolbar

const ParameterTracking = ({ parameterName, stockName=null, previewMode=false, onPointClick=null, currentDbValue=null }) => {

  const CHART_HEIGHT = 500;
  const [parameterData, setParameterData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableFilters, setAvailableFilters] = useState({});
  const [dateFilters, setDateFilters] = useState({
    startDate: null,
    endDate: null
  });
  const [selectedFilters, setSelectedFilters] = useState({});
  const [chartData, setChartData] = useState([]);
  const [rollingAverage, setRollingAverage] = useState([]);
  const [highlightedPoint, setHighlightedPoint] = useState(null);
  const { getBasParameterTracking } = useApi();
  const [parameterCache, setParameterCache] = useState({});

  // NEW - For paper ranking
  const [showRankingToolbar, setShowRankingToolbar] = useState(false);
  const [paperRankings, ] = useState({});
  const [isRankingLoading, ] = useState(false);

  // D3-specific refs and state
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [, setContainerWidth] = useState(0);
  const isHoveringRef = useRef(false);

  // Wrap getPointSize in useCallback to prevent recreation on every render
  const getPointSize = useCallback((paperId) => {
    try {
      const baseSize = 4;
      const maxSize = 24;
      
      if (paperId && paperRankings[paperId]) {
        // Safely convert ranking to number if needed
        let ranking = paperRankings[paperId];
        if (typeof ranking !== 'number') {
          ranking = parseFloat(String(ranking));
          if (isNaN(ranking)) return baseSize;
        }
        
        // Apply a power function to the score (e.g., square it)
        const amplifiedScore = Math.pow(ranking, 2);
        return baseSize + (amplifiedScore * (maxSize - baseSize));
      }
      
      return baseSize;
    } catch (e) {
      console.warn("Error in getPointSize:", e);
      return 6; // Default size if there's an error
    }
  }, [paperRankings]);

  // Set up resize observer for container width changes
  useEffect(() => {
    // Function to measure and set the container width
    const measureContainer = () => {
      if (containerRef.current) {
        const width = containerRef.current.getBoundingClientRect().width;
        if (width > 0) {
          setContainerWidth(width);
        } else {
          // If width is 0, try to get width from parent element
          const parent = containerRef.current.parentElement;
          if (parent) {
            const parentWidth = parent.getBoundingClientRect().width;
            if (parentWidth > 0) {
              // Apply a reasonable estimate based on parent width
              const estimatedWidth = parentWidth * 0.75; // Similar to CSS calc
              setContainerWidth(estimatedWidth);
            }
          }
        }
      }
    };

    // Create a ResizeObserver with debouncing and hover protection
    let resizeTimeout;
    const resizeObserver = new ResizeObserver(() => {
      // Don't trigger resize during hover events
      if (isHoveringRef.current) return;
      
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        measureContainer();
      }, 100);
    });
    
    // Create a MutationObserver to detect when the component is added to DOM
    const mutationObserver = new MutationObserver(() => {
      measureContainer();
    });
    
    // Start observing resize events
    // Store containerRef.current in a variable for cleanup
    const currentContainer = containerRef.current;
    
    if (currentContainer) {
      resizeObserver.observe(currentContainer);
      
      // Also observe the parent element for mutations
      const parent = currentContainer.parentElement;
      if (parent) {
        mutationObserver.observe(parent, { 
          childList: true,
          subtree: true,
          attributes: true
        });
      }
    }
    
    // Initial measurement right away
    measureContainer();
    
    // Multiple measurement attempts with increasing delays
    const initialTimer1 = setTimeout(measureContainer, 50);
    const initialTimer2 = setTimeout(measureContainer, 200);
    const initialTimer3 = setTimeout(measureContainer, 500);
    const initialTimer4 = setTimeout(measureContainer, 1000);
    
    // Add window resize listener as additional safety
    window.addEventListener('resize', measureContainer);
    
    // Clean up
    return () => {
      clearTimeout(initialTimer1);
      clearTimeout(initialTimer2);
      clearTimeout(initialTimer3);
      clearTimeout(initialTimer4);
      clearTimeout(resizeTimeout);
      
      if (currentContainer) {
        resizeObserver.unobserve(currentContainer);
      }
      
      mutationObserver.disconnect();
      window.removeEventListener('resize', measureContainer);
    };
  }, []);

  // Update the calculateRollingAverage function to use standard deviation
  const calculateRollingAverage = useCallback((data) => {
    if (data.length === 0) return [];
    
    // 36-month window in milliseconds
    const windowSize = 2 * 365 * 24 * 60 * 60 * 1000;
    const rollingData = [];
    
    // Create a timeline covering all data points with monthly intervals
    const sortedByTime = [...data].sort((a, b) => a.timestamp - b.timestamp);
    const firstDate = sortedByTime[0].timestamp;
    const lastDate = sortedByTime[sortedByTime.length - 1].timestamp;
    
    // Generate data points at monthly intervals
    const monthInterval = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds
    let currentDate = firstDate;
    
    while (currentDate <= lastDate) {
      const windowStart = currentDate - windowSize/2;
      const windowEnd = currentDate + windowSize/2;
      
      // Find all points in the window
      const pointsInWindow = data.filter(p => 
          p.timestamp >= windowStart && p.timestamp <= windowEnd
      );
      
      // Calculate metrics if there are points in the window
      if (pointsInWindow.length > 0) {
          const values = pointsInWindow.map(p => p.value);
          
          // Calculate mean
          const mean = values.reduce((sum, v) => sum + v, 0) / values.length;
          
          // Calculate standard deviation
          const squareDiffs = values.map(value => {
          const diff = value - mean;
          return diff * diff;
          });
          const avgSquareDiff = squareDiffs.reduce((sum, squared) => sum + squared, 0) / squareDiffs.length;
          const stdDev = Math.sqrt(avgSquareDiff);
          
          // Create date string for chart
          const dateObj = new Date(currentDate);
          const dateStr = dateObj.toISOString().split('T')[0];
          
          rollingData.push({
          date: dateStr,
          timestamp: currentDate,
          average: mean,
          stdDev: stdDev,
          upperBound: mean + stdDev,  // +1 standard deviation
          lowerBound: Math.max(0, mean - stdDev),  // -1 standard deviation, but never below zero
          min: Math.min(...values),
          max: Math.max(...values),
          pointCount: pointsInWindow.length
          });
      }
      
      // Move to next month
      currentDate += monthInterval;
    }
    
    setRollingAverage(rollingData);
  }, []);

  // NEW - Function to fetch paper rankings
  // const fetchPaperRankings = async (paperData) => {
  //   try {
  //     console.log("Starting to fetch paper rankings for papers:", paperData.map(p => p.paperId));
  //     setIsRankingLoading(true);
      
  //     // Extract paper IDs from data points
  //     const paperIds = [];
  //     const paperIdMap = {};
      
  //     paperData.forEach(dataPoint => {
  //       if (dataPoint.paperId && !paperIdMap[dataPoint.paperId]) {
  //         const id = parseInt(dataPoint.paperId, 10);
  //         if (!isNaN(id)) {
  //           paperIds.push(id);
  //           paperIdMap[dataPoint.paperId] = true;
  //         }
  //       }
  //     });
      
  //     if (paperIds.length === 0) {
  //       console.log("No valid paper IDs to rank");
  //       setIsRankingLoading(false);
  //       return;
  //     }
      
  //     console.log(`Extracted ${paperIds.length} valid paper IDs for ranking`);
  //     console.log("Fetching rankings for paper IDs:", paperIds);
  //     console.log("Sending ranking request with weights:", rankingWeights);
      
  //     // // Call the ranking API
  //     // const result = await callApi('/rank-papers', {
  //     //   method: 'POST',
  //     //   body: JSON.stringify({
  //     //     paper_ids: paperIds,
  //     //     weights: rankingWeights,
  //     //     debug: false
  //     //   }),
  //     // });
      
  //     // console.log("Ranking API response:", result);
      
  //     // Process the results into a lookup table by ID
  //     // const rankingsMap = {};
  //     // if (result && result.ranked_papers) {
  //     //   result.ranked_papers.forEach(paper => {
  //     //     if (paper.paper_id) {
  //     //       rankingsMap[paper.paper_id] = paper.score;
  //     //     }
  //     //   });
  //     // }
      
  //     console.log("Processed paper rankings:", rankingsMap);
  //     setPaperRankings(rankingsMap);
  //     setIsRankingLoading(false);
      
  //     // Process data for chart with new rankings - do not call directly here
  //     // Let the useEffect that watches paperRankings handle this
      
  //   } catch (error) {
  //     console.error("Error fetching paper rankings:", error);
  //     setIsRankingLoading(false);
  //   }
  // };

  // NEW - Handler for ranking weight changes
    // Handler for ranking weight changes
// const handleWeightsChange = (newWeights) => {
//   console.log("Weights changed from:", rankingWeights, "to:", newWeights);
  
//   // Check if this is a special "uniform small size" request
//   if (newWeights.useMinimalSizes) {
//     console.log("Setting uniform small size for all papers");
    
//     // Create a new paperRankings object with minimal values for all papers
//     const minimalRankings = {};
    
//     // Set a minimal ranking for all papers in chartData
//     chartData.forEach(point => {
//       if (point.paperId) {
//         minimalRankings[point.paperId] = 0.01;
//       }
//     });
    
//     // Update the rankings directly
//     setPaperRankings(minimalRankings);
    
//     // Keep the weight sliders as they were
//     return;
//   }
  
//   // Normal weight change - update the weights
//   setRankingWeights(newWeights);
  
//   // Re-fetch rankings with new weights
//   if (chartData.length > 0) {
//     fetchPaperRankings(chartData);
//   }
// };

  // Process data for chart
  const processDataForChart = useCallback((data) => {
    if (!data || !data.results) return;
  
    const allDataPoints = [];
    const allDatesDebug = [];
    
    // Extract all data points from the papers
    data.results.forEach(paper => {
      // Debug: log publication_date
      allDatesDebug.push(paper.publication_date);
      // Skip invalid publication dates
      if (!paper.publication_date) {
        console.warn('Skipping paper with missing publication_date:', paper);
        return;
      }
      
      // Create date object safely
      let publicationDate;
      try {
        publicationDate = new Date(paper.publication_date);
        // Check for invalid date
        if (isNaN(publicationDate.getTime())) {
          console.warn('Invalid publication_date:', paper.publication_date, 'Paper:', paper);
          return;
        }
      } catch (e) {
        console.warn('Exception parsing publication_date:', paper.publication_date, 'Paper:', paper, e);
        return;
      }
      
      // Apply date filters if they are set
      if (
        (dateFilters.startDate && publicationDate < new Date(dateFilters.startDate)) ||
        (dateFilters.endDate && publicationDate > new Date(dateFilters.endDate))
      ) {
        return; // Skip this paper if it's outside the date range
      }
      
      paper.extraction.results.forEach(result => {
        // Check if this point passes all filters
        let passesFilters = true;
        
        // Check each active filter
        Object.keys(selectedFilters).forEach(filterKey => {
          // TODO: Revisit this logic if we want different filter semantics
          // If the filter array is empty, do NOT filter out any data (i.e., include all)
          if (selectedFilters[filterKey].length > 0) {
            // Handle location fields
            if (filterKey === 'country' || filterKey === 'region') {
              const locationValue = result.location && result.location[filterKey];
              if (!locationValue || !selectedFilters[filterKey].includes(locationValue)) {
                passesFilters = false;
              }
            } 
            // Handle array-type fields (like species)
            else if (Array.isArray(result[filterKey])) {
              if (!result[filterKey] || !result[filterKey].some(val => selectedFilters[filterKey].includes(val))) {
                passesFilters = false;
              }
            }
            // Handle scalar fields
            else {
              if (!result[filterKey] || !selectedFilters[filterKey].includes(result[filterKey])) {
                passesFilters = false;
              }
            }
          }
          // If filter array is empty, do nothing (include all)
        });
        
        if (!passesFilters) return; // Skip this data point if it doesn't match filters
        
        // Only use the 'value' field, not raw_value
        const value = parseFloat(result.value);
        if (isNaN(value)) return;
        
        // Extract range if available
        let lowerRange = null;
        let upperRange = null;
        if (result.range && result.range.length >= 2) {
          lowerRange = result.range[0];
          upperRange = result.range[1];
        }
        
        // Get all filterable properties for tooltips
        const tooltipData = {
          paperTitle: paper.paper_title,
          location: result.location?.country || 'Unknown location',
          raw_value: result.raw_value || '',
          units: data.standard_unit || ''
        };
        
        // Add all available filter fields to tooltip
        Object.keys(availableFilters).forEach(field => {
          if (field === 'country' || field === 'region') {
            // Skip - already handled in location
          } else if (Array.isArray(result[field])) {
            tooltipData[field] = result[field].join(', ');
          } else if (result[field]) {
            tooltipData[field] = result[field];
          }
        });
        
        allDataPoints.push({
          date: publicationDate,
          timestamp: publicationDate.getTime(),
          value: value,
          lowerRange: lowerRange,
          upperRange: upperRange,
          paperTitle: paper.paper_title,
          paperId: paper.paper_id, // NEW - Include paper ID for ranking
          location: result.location?.country || 'Unknown location',
          tooltip: tooltipData
        });
      });
    });
    
    // Sort by date
    allDataPoints.sort((a, b) => a.timestamp - b.timestamp);
    
    // Format for chart
    const formattedData = allDataPoints.map(point => ({
        date: point.date.toISOString().split('T')[0], // Convert to YYYY-MM-DD format
        timestamp: point.timestamp,
        value: point.value,
        lowerRange: point.lowerRange,
        upperRange: point.upperRange,
        paperId: point.paperId, // NEW - Include paper ID for ranking
        errorY: [
          point.lowerRange ? point.value - point.lowerRange : 0,
          point.upperRange ? point.upperRange - point.value : 0
        ],
        tooltip: point.tooltip,
        // Calculate point size here instead of relying on getPointSize
        pointSize: getPointSize(point.paperId)
    }));
    setChartData(formattedData);
  
    // Calculate rolling average and quartile ranges
    if (formattedData.length > 0) {
      calculateRollingAverage(formattedData);
    }
  }, [dateFilters, selectedFilters, calculateRollingAverage, availableFilters, getPointSize]);
  // Removed getPointSize from dependencies to avoid circular dependency

  // Fetch parameter data when selected parameter changes
  useEffect(() => {
    if (!parameterName) return;

    const fetchParameterData = async () => {
      // Check cache first to prevent infinite requests
      if (parameterCache[parameterName]) {
        console.log(`Using cached data for parameter: ${parameterName}`);
        setParameterData(parameterCache[parameterName]);
        processDataForChart(parameterCache[parameterName]);
        return;
      }
      
      setIsLoading(true);
      setError(null);

      try {
        console.log(`Fetching data for parameter: ${parameterName}`);
        var response = await getBasParameterTracking(parameterName);
        var data = response.data;
        console.log("Parameter tracking data:", data);
        
        // Store in cache to prevent repeated requests
        setParameterCache(prev => ({
          ...prev,
          [parameterName]: data
        }));
        
        setParameterData(data);
        
        // Extract available filter options
        const filterOptions = {};
        
        // Always check for location fields (country, region)
        const countryOptions = new Set();
        const regionOptions = new Set();
        
        // Track all fields that appear in the data for potential filtering
        const seenFields = new Set();
        
        data.results.forEach(paper => {
          paper.extraction.results.forEach(result => {
            // Identify all available fields in the data
            Object.keys(result).forEach(field => {
              // Skip standard non-filterable fields
              if (['value', 'raw_value', 'range', 'additional_factors'].includes(field)) {
                return;
              }
              
              seenFields.add(field);
              
              // Handle location fields specially
              if (field === 'location') {
                if (result.location?.country) {
                  countryOptions.add(result.location.country);
                }
                if (result.location?.region) {
                  regionOptions.add(result.location.region);
                }
              } 
              // Handle array fields
              else if (Array.isArray(result[field]) && result[field].length > 0) {
                if (!filterOptions[field]) {
                  filterOptions[field] = new Set();
                }
                result[field].forEach(item => {
                  if (item) filterOptions[field].add(item);
                });
              }
              // Handle other fields with simple values
              else if (result[field] && typeof result[field] === 'string') {
                if (!filterOptions[field]) {
                  filterOptions[field] = new Set();
                }
                filterOptions[field].add(result[field]);
              }
            });
          });
        });
        
        // Add location fields if they have values
        if (countryOptions.size > 0) {
          filterOptions.country = Array.from(countryOptions).sort();
        }
        if (regionOptions.size > 0) {
          filterOptions.region = Array.from(regionOptions).sort();
        }
        
        // Convert all other sets to sorted arrays
        Object.keys(filterOptions).forEach(field => {
          if (filterOptions[field] instanceof Set) {
            filterOptions[field] = Array.from(filterOptions[field]).sort();
          }
        });
        
        console.log("Available filter options:", filterOptions);
        setAvailableFilters(filterOptions);
        
        // Initialize the selected filters structure
        const initialSelectedFilters = {};
        Object.keys(filterOptions).forEach(field => {
          initialSelectedFilters[field] = [];
        });
        setSelectedFilters(initialSelectedFilters);
        
        // Process data for chart
        processDataForChart(data);

      } catch (err) {
        console.error('Error fetching parameter data:', err);
        setError('Failed to load parameter data. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchParameterData();
  }, [parameterName, processDataForChart, getBasParameterTracking, parameterCache]);
  
  // Process data when filters or paper rankings change
  useEffect(() => {
    if (parameterData) {
      console.log("Processing data with updated filters or rankings");
      processDataForChart(parameterData);
    }
  }, [dateFilters, selectedFilters, parameterData, processDataForChart, paperRankings]);

  // Helper function to retrieve the standard unit
  const getStandardUnit = useCallback(() => {
    if (!parameterData) return '';
    return parameterData.standard_unit ? `(${parameterData.standard_unit})` : '';
  }, [parameterData]);

  // Apply chart container styling
  useEffect(() => {
    // Apply a minimum width to the chart container
    if (containerRef.current) {
      containerRef.current.style.minWidth = '700px';
    }
  }, []);

  // Debugging version with visible boundaries and console logging
  // D3 visualization effect
useEffect(() => {
  if (!svgRef.current || chartData.length === 0) return;
  
  // Function to render the chart with proper width calculation
  const renderChart = () => {
    // Clear any existing visualization
    d3.select(svgRef.current).selectAll('*').remove();
    
    // Get the actual container width and height from DOM
    const containerElement = svgRef.current.parentElement;
    const containerRect = containerElement.getBoundingClientRect();
    
    // More aggressive width calculation with multiple fallbacks
    let containerWidth = containerRect.width;
    
    // If width is 0 or very small, try multiple fallback strategies
    if (containerWidth <= 0 || containerWidth < 100) {
      // Strategy 1: Try parent element
      const parentElement = containerElement.parentElement;
      if (parentElement) {
        const parentRect = parentElement.getBoundingClientRect();
        if (parentRect.width > 0) {
          containerWidth = parentRect.width * 0.9; // Use 90% of parent width
        }
      }
      
      // Strategy 2: Try grandparent element
      if (containerWidth <= 0 && parentElement?.parentElement) {
        const grandparentRect = parentElement.parentElement.getBoundingClientRect();
        if (grandparentRect.width > 0) {
          containerWidth = grandparentRect.width * 0.8;
        }
      }
      
      // Strategy 3: Use window width as fallback
      if (containerWidth <= 0) {
        containerWidth = Math.min(window.innerWidth * 0.6, 1000); // 60% of window width, max 1000px
      }
      
      // Strategy 4: Absolute fallback
      if (containerWidth <= 0) {
        containerWidth = 800; // Reasonable default
      }
    }
    
    console.log('Chart container width:', containerWidth, 'Container rect:', containerRect);
    
    // use fixed height to avoid SVG/DOM reflow when elements (like tooltips) appear
    const containerHeight = CHART_HEIGHT;

  // Set up margins with LARGER right margin to prevent overflow
  const margin = { top: 20, right: 80, left: 80, bottom: 120 };
  
  // Calculate chart dimensions
  const width = containerWidth - margin.left - margin.right;
  const height = containerHeight - margin.top - margin.bottom;
  
  // Create SVG with explicit dimensions
  const svg = d3.select(svgRef.current)
    .attr('width', containerWidth)
    .attr('height', containerHeight);
  
  // Create a clipping path to prevent overflow
  svg.append('defs')
    .append('clipPath')
    .attr('id', 'chart-area-clip')
    .append('rect')
    .attr('width', width)
    .attr('height', height);
  
  // Add gradients for the standard deviation area
  const defs = svg.select('defs');
  const gradient = defs.append('linearGradient')
    .attr('id', 'stdDevGradient')
    .attr('x1', '0%')
    .attr('y1', '0%')
    .attr('x2', '0%')
    .attr('y2', '100%');
  
  gradient.append('stop')
    .attr('offset', '5%')
    .attr('stop-color', '#3b82f6')
    .attr('stop-opacity', 0.3);
  
  gradient.append('stop')
    .attr('offset', '95%')
    .attr('stop-color', '#3b82f6')
    .attr('stop-opacity', 0.1);
  
  // Main chart group with transform
  const g = svg.append('g')
    .attr('transform', `translate(${margin.left}, ${margin.top})`);
  
  // Chart content group with clipping
  const chartGroup = g.append('g')
    .attr('class', 'chart-area-clip')
    .attr('clip-path', 'url(#chart-area-clip)');

  // Remove custom x-axis logic for 'M' units. Restore to original:
  const xExtent = d3.extent(chartData, d => d.timestamp);
  const xDomainPadding = (xExtent[1] - xExtent[0]) * 0.05;
  const xScale = d3.scaleTime()
    .domain([
      xExtent[0] - xDomainPadding,
      xExtent[1] + xDomainPadding
    ])
    .range([0, width - 10]); // Use slightly reduced width for safety
  
  const yScale = d3.scaleLinear()
    .domain([0, d3.max(chartData, d => d.value) * 1.1]) // Add 10% on top
    .range([height, 0]);
  
  // Create and add x-axis
  const xAxis = d3.axisBottom(xScale)
    .tickFormat(d => {
      const date = new Date(d);
      return date.toISOString().split('T')[0];
    });
  
  g.append('g')
    .attr('transform', `translate(0, ${height})`)
    .call(xAxis)
    .selectAll('text')
    .attr('transform', 'rotate(-45)')
    .style('text-anchor', 'end')
    .attr('dx', '-.8em')
    .attr('dy', '.15em')
    .style('fill', '#6b7280');
  
  // Add x-axis label
  g.append('text')
    .attr('transform', `translate(${width / 2}, ${height + 70})`)
    .style('text-anchor', 'middle')
    .style('fill', '#6b7280')
    .text('Publication Date');
  
  // Create and add y-axis with larger font size
  g.append('g')
    .call(d3.axisLeft(yScale))
    .selectAll('text')
    .style('fill', '#cccccc')
    .style('font-size', '14px'); // Increased font size
  
  // Add y-axis label
  g.append('text')
    .attr('transform', 'rotate(-90)')
    .attr('y', -60)
    .attr('x', -height / 2)
    .attr('dy', '1em')
    .style('text-anchor', 'middle')
    .style('fill', '#6b7280')
    .text(() => {
      // TODO: Remove this hacky code - append Atlantic Cod to specific parameters
      let displayName = parameterName;
      if (parameterName === 'Natural Mortality Rate' || parameterName === 'Fishing Mortality') {
        displayName = `${parameterName} (Atlantic Cod)`;
      }
      return `${displayName} ${getStandardUnit()}`;
    });
  
  // Add grid lines
  g.append('g')
    .attr('class', 'grid-lines')
    .selectAll('line')
    .data(yScale.ticks(5))
    .enter()
    .append('line')
    .attr('x1', 0)
    .attr('x2', width - 10) // Make grid lines slightly shorter for safety
    .attr('y1', d => yScale(d))
    .attr('y2', d => yScale(d));
  
  // Create tooltip div if it doesn't exist
  let tooltip = d3.select('body').select('.d3-tooltip');
  if (tooltip.empty()) {
    tooltip = d3.select('body').append('div')
      .attr('class', 'd3-tooltip custom-tooltip')
      .style('opacity', 0)
      .style('position', 'absolute')
      .style('pointer-events', 'none');
  }
  
  // Add rolling average area if we have data
  if (rollingAverage.length > 0) {
    // Area between upper and lower bounds
    const areaGenerator = d3.area()
      .x(d => xScale(d.timestamp))
      .y0(d => yScale(d.lowerBound))
      .y1(d => yScale(d.upperBound))
      .curve(d3.curveMonotoneX);
    
    chartGroup.append('path')
      .datum(rollingAverage)
      .attr('class', 'std-dev-area')
      .attr('fill', 'url(#stdDevGradient)')
      .attr('d', areaGenerator);
    
    // Upper bound line
    const upperLineGenerator = d3.line()
      .x(d => xScale(d.timestamp))
      .y(d => yScale(d.upperBound))
      .curve(d3.curveMonotoneX);
    
    chartGroup.append('path')
      .datum(rollingAverage)
      .attr('class', 'std-dev-line')
      .attr('fill', 'none')
      .attr('stroke', '#3b82f6')
      .attr('stroke-opacity', 0.5)
      .attr('stroke-width', 1)
      .attr('d', upperLineGenerator);
    
    // Lower bound line
    const lowerLineGenerator = d3.line()
      .x(d => xScale(d.timestamp))
      .y(d => yScale(d.lowerBound))
      .curve(d3.curveMonotoneX);
    
    chartGroup.append('path')
      .datum(rollingAverage)
      .attr('class', 'std-dev-line')
      .attr('fill', 'none')
      .attr('stroke', '#3b82f6')
      .attr('stroke-opacity', 0.5)
      .attr('stroke-width', 1)
      .attr('d', lowerLineGenerator);
    
    // Rolling average line
    const lineGenerator = d3.line()
      .x(d => xScale(d.timestamp))
      .y(d => yScale(d.average))
      .curve(d3.curveMonotoneX);
    
    chartGroup.append('path')
      .datum(rollingAverage)
      .attr('class', 'std-dev-line average')
      .attr('fill', 'none')
      .attr('stroke', '#3b82f6')
      .attr('stroke-width', 1.5)
      .attr('d', lineGenerator);
    
    // Add rolling average hover points with tooltips
    chartGroup.selectAll('.avg-point')
      .data(rollingAverage)
      .enter()
      .append('circle')
      .attr('class', 'avg-point')
      .attr('cx', d => xScale(d.timestamp))
      .attr('cy', d => yScale(d.average))
      .attr('r', 0) // Invisible but still interactive
      .attr('fill', 'transparent')
      .on('mouseover', function(event, d) {
        try {
          const tooltipContent = `
            <p class="tooltip-title">36-month Rolling Average</p>
            <p class="tooltip-date"><b>Date:</b> ${d.date}</p>
            <p class="tooltip-value"><b>Average:</b> ${Number(d.average).toFixed(2)}</p>
            <p class="tooltip-value"><b>Standard Deviation:</b> ${Number(d.stdDev).toFixed(2)}</p>
            <p class="tooltip-value"><b>Coefficient of Variation:</b> ${(Number(d.stdDev) / Number(d.average) * 100).toFixed(1)}%</p>
            <p class="tooltip-count"><b>Data Points in Window:</b> ${d.pointCount}</p>
          `;
          
          tooltip
            .html(tooltipContent)
            .style('left', `${event.clientX + 10}px`)
            .style('top', `${event.clientY - 10}px`)
            .style('opacity', 1);
        } catch (e) {
          console.warn("Error showing rolling average tooltip:", e);
        }
      })
      .on('mouseout', function() {
        tooltip.style('opacity', 0);
      });
  }
  
  // Add range lines for data points with ranges
  chartData.forEach(point => {
    if (point.lowerRange != null && point.upperRange != null) {
      try {
        // Convert values to numbers safely
        let lowerVal, upperVal;
        
        // First check if they're already numbers
        if (typeof point.lowerRange === 'number') {
          lowerVal = point.lowerRange;
        } else {
          // Try to convert to number, with fallback
          try {
            lowerVal = parseFloat(String(point.lowerRange));
            if (isNaN(lowerVal)) return; // Skip if not a valid number
          } catch (e) {
            return; // Skip if conversion fails
          }
        }
        
        // Same for upper range
        if (typeof point.upperRange === 'number') {
          upperVal = point.upperRange;
        } else {
          try {
            upperVal = parseFloat(String(point.upperRange));
            if (isNaN(upperVal)) return;
          } catch (e) {
            return;
          }
        }
        
        // Only draw if we have a valid timestamp
        if (!point.timestamp) return;
        
        chartGroup.append('line')
          .attr('class', 'range-line')
          .attr('x1', xScale(point.timestamp))
          .attr('x2', xScale(point.timestamp))
          .attr('y1', yScale(lowerVal))
          .attr('y2', yScale(upperVal));
      } catch (e) {
        console.warn("Error drawing range line:", e);
        // Skip this range line on error
      }
    }
  });
  
  // Add horizontal line for current database value if provided
  if (currentDbValue != null && !isNaN(currentDbValue)) {
    const dbValueY = yScale(currentDbValue);
    
    // Add the horizontal line
    chartGroup.append('line')
      .attr('class', 'db-value-line')
      .attr('x1', 0)
      .attr('x2', width - 10)
      .attr('y1', dbValueY)
      .attr('y2', dbValueY)
      .attr('stroke', '#ff6b35')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '5,5')
      .attr('opacity', 0.8);
    
    // Add label for the database value line
    chartGroup.append('text')
      .attr('class', 'db-value-label')
      .attr('x', width - 15)
      .attr('y', dbValueY - 5)
      .attr('text-anchor', 'end')
      .attr('fill', '#ff6b35')
      .attr('font-size', '12px')
      .attr('font-weight', 'bold')
      .text(`Current: ${currentDbValue.toFixed(3)}`);
  }

  // Add data points
  chartGroup.selectAll('.data-point')
    .data(chartData)
    .enter()
    .append('circle')
    .attr('class', d => {
      // If this is the highlighted point, add 'highlighted' class
      return highlightedPoint && highlightedPoint.timestamp === d.timestamp && 
             highlightedPoint.value === d.value ? 
             'data-point highlighted' : 'data-point';
    })
    .attr('cx', d => {
      // Ensure valid timestamp - use 0 if invalid
      if (!d.timestamp) return 0;
      return xScale(d.timestamp);
    })
    .attr('cy', d => {
      // Ensure valid value - use 0 if invalid
      if (d.value == null || isNaN(d.value)) return height;
      return yScale(d.value);
    })
    .attr('r', function(d) {
      try {
        return getPointSize(d.paperId);
      } catch (e) {
        // Default size if there's an error
        return 4;
      }
    })
    .on('mouseover', function(event, d) {
      try {
        // Set hover flag to prevent resize observer from triggering
        isHoveringRef.current = true;
        
        // Highlight the point using CSS class
        d3.select(this)
          .classed('highlighted', true);
        
        // Update highlighted point state
        setHighlightedPoint(d);
        
        // Create tooltip content
        let tooltipContent = `
          <p class="tooltip-title">${d.tooltip?.paperTitle || 'Unknown'}</p>
          <p class="tooltip-date"><b>Published:</b> ${d.date || 'Unknown date'}</p>
        `;
        
        // Safely add value with type checking
        if (d.value != null) {
          try {
            const valueStr = typeof d.value === 'number' ? 
              Number(d.value).toFixed(2) : String(d.value);
            if (parameterName === 'Natural Mortality Rate') {
              if (d.lowerRange != null && d.upperRange != null && !isNaN(d.lowerRange) && !isNaN(d.upperRange)) {
                tooltipContent += `<p class="tooltip-value"><b>Value:</b> (<i>M</i>=${d.lowerRange} to ${d.upperRange})</p>`;
              } else {
                tooltipContent += `<p class="tooltip-value"><b>Value:</b> (<i>M</i>=${valueStr})</p>`;
              }
            } else if (parameterName === 'Fishing Mortality') {
              if (d.lowerRange != null && d.upperRange != null && !isNaN(d.lowerRange) && !isNaN(d.upperRange)) {
                tooltipContent += `<p class="tooltip-value"><b>Value:</b> (<i>F</i>=${d.lowerRange} to ${d.upperRange})</p>`;
              } else {
                tooltipContent += `<p class="tooltip-value"><b>Value:</b> (<i>F</i>=${valueStr})</p>`;
              }
            } else {
              tooltipContent += `<p class="tooltip-value"><b>Value:</b> ${valueStr} ${d.tooltip?.units || ''}</p>`;
            }
          } catch (e) {
            tooltipContent += `<p class="tooltip-value"><b>Value:</b> ${String(d.value)}</p>`;
          }
        }
        // Add ranking score if available - with type checking
        if (d.paperId && paperRankings[d.paperId]) {
          try {
            const rankingScore = paperRankings[d.paperId];
            let scoreStr;
            if (typeof rankingScore === 'number') {
              scoreStr = `${(rankingScore * 100).toFixed(0)}%`;
            } else {
              scoreStr = String(rankingScore);
            }
            tooltipContent += `
              <p class="tooltip-ranking" style="color: #FF5500; font-weight: bold">
                <b>Ranking Score:</b> ${scoreStr}
              </p>
            `;
          } catch (e) {
            console.warn("Error formatting ranking score:", e);
          }
        }
        // Add range if available - with thorough type checking to avoid the error
        // Only show this for non-Natural Mortality Rate parameters
        if (parameterName !== 'Natural Mortality Rate' && d.lowerRange != null && d.upperRange != null) {
          try {
            // Try to convert values to numbers
            let lowerVal, upperVal, lowerStr, upperStr;
            if (typeof d.lowerRange === 'number') {
              lowerVal = d.lowerRange;
              lowerStr = Number(lowerVal).toFixed(2);
            } else {
              lowerStr = String(d.lowerRange);
            }
            if (typeof d.upperRange === 'number') {
              upperVal = d.upperRange;
              upperStr = Number(upperVal).toFixed(2);
            } else {
              upperStr = String(d.upperRange);
            }
            tooltipContent += `
              <p class="tooltip-range">
                <b>Range:</b> ${lowerStr} - ${upperStr} ${d.tooltip?.units || ''}
              </p>
            `;
          } catch (e) {
            console.warn("Error processing range values:", e);
            tooltipContent += `
              <p class="tooltip-range">
                <b>Range:</b> Available
              </p>
            `;
          }
        }
        
        // Add other tooltip fields dynamically with careful validation
        if (d.tooltip) {
          Object.entries(d.tooltip).forEach(([key, value]) => {
            // Skip fields already handled or empty values
            if (['paperTitle', 'units', 'raw_value'].includes(key) || !value) return;
            
            tooltipContent += `
              <p class="tooltip-${key}">
                <b>${key.charAt(0).toUpperCase() + key.slice(1)}:</b> ${String(value)}
              </p>
            `;
          });
        }
        
        // Show tooltip
        tooltip
          .html(tooltipContent)
          .style('left', `${event.clientX + 10}px`)
          .style('top', `${event.clientY - 10}px`)
          .style('opacity', 1);
      } catch (e) {
        console.warn("Error in mouseover handler:", e);
      }
    })
    .on('mouseout', function() {
      try {
        // Clear hover flag
        isHoveringRef.current = false;
        
        // Restore original appearance using CSS class
        d3.select(this)
          .classed('highlighted', false);
        
        // Hide tooltip
        tooltip.style('opacity', 0);
        
        // Use timeout to prevent flickering
        setTimeout(() => {
          setHighlightedPoint(null);
        }, 50);
      } catch (e) {
        console.warn("Error in mouseout handler:", e);
      }
    })
    .on('click', function(event, d) {
      try {
        console.log('Data point clicked!', d);
        console.log('onPointClick function available:', !!onPointClick);
        
        // Handle click to update natural mortality value
        if (onPointClick) {
          let mValue = null;
          
          // Try to get value from tooltip first, but only if it's a valid number
          if (d.tooltip && d.tooltip.raw_value != null && !isNaN(parseFloat(d.tooltip.raw_value))) {
            mValue = parseFloat(d.tooltip.raw_value);
            console.log('Using tooltip raw_value:', mValue);
          }
          // Fallback to direct value
          else if (d.value != null && !isNaN(parseFloat(d.value))) {
            mValue = parseFloat(d.value);
            console.log('Using direct value:', mValue);
          }
          // Handle ranges - use midpoint
          else if (d.lowerRange != null && d.upperRange != null && 
                   !isNaN(parseFloat(d.lowerRange)) && !isNaN(parseFloat(d.upperRange))) {
            mValue = (parseFloat(d.lowerRange) + parseFloat(d.upperRange)) / 2;
            console.log('Using range midpoint:', mValue, 'from range', d.lowerRange, 'to', d.upperRange);
          }
          
          if (mValue != null && !isNaN(mValue)) {
            console.log('Calling onPointClick with M value:', mValue);
            onPointClick(mValue);
          } else {
            console.log('Could not extract M value from point. Data structure:', d);
          }
        } else {
          console.log('onPointClick function not provided');
        }
      } catch (e) {
        console.warn("Error in click handler:", e);
      }
    });
  }; // End of renderChart function
  
  // Try to render immediately
  renderChart();
  
  // Always set up retry timeouts to handle modal rendering delays
  const retryTimeout1 = setTimeout(() => {
    console.log('Retry 1: Re-rendering chart');
    renderChart();
  }, 50);
  
  const retryTimeout2 = setTimeout(() => {
    console.log('Retry 2: Re-rendering chart');
    renderChart();
  }, 200);
  
  const retryTimeout3 = setTimeout(() => {
    console.log('Retry 3: Re-rendering chart');
    renderChart();
  }, 500);
  
  const retryTimeout4 = setTimeout(() => {
    console.log('Retry 4: Re-rendering chart');
    renderChart();
  }, 1000);
  
  return () => {
    clearTimeout(retryTimeout1);
    clearTimeout(retryTimeout2);
    clearTimeout(retryTimeout3);
    clearTimeout(retryTimeout4);
  };
}, [chartData, rollingAverage, highlightedPoint, paperRankings, getPointSize, parameterName, getStandardUnit, onPointClick, currentDbValue]);

  const handleFilterChange = (filterType, value) => {
    setSelectedFilters(prev => {
      const currentFilters = [...prev[filterType]];
      
      if (currentFilters.includes(value)) {
        // Remove the value if already selected
        return {
          ...prev,
          [filterType]: currentFilters.filter(v => v !== value)
        };
      } else {
        // Add the value if not already selected
        return {
          ...prev,
          [filterType]: [...currentFilters, value]
        };
      }
    });
  };

  const handleDateFilterChange = (filterType, value) => {
    setDateFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const renderFilterSection = (filterType, options) => (
    <div className="filter-section" key={`filter-${filterType}`}>
      <h4>{filterType.charAt(0).toUpperCase() + filterType.slice(1)}</h4>
      <div className="filter-options">
        {options.map(option => (
          <label key={option} className="filter-option">
            <input
              type="checkbox"
              checked={selectedFilters[filterType]?.includes(option) || false}
              onChange={() => handleFilterChange(filterType, option)}
            />
            {option}
          </label>
        ))}
      </div>
    </div>
  );

  // Create a component for the date filter section
  const DateFilterSection = () => {
    // Get min/max dates from chart data for range restrictions
    const getMinMaxDates = () => {
      if (parameterData?.results?.length > 0) {
        const dates = parameterData.results
          .map(paper => new Date(paper.publication_date))
          .filter(date => !isNaN(date.getTime()))
          .sort((a, b) => a - b);
        
        if (dates.length > 0) {
          // Always use at least 1975-01-01 as the minimum
          const minDate = new Date(Math.min(dates[0].getTime(), new Date('1975-01-01').getTime()));
          return {
            min: minDate.toISOString().split('T')[0],
            max: dates[dates.length - 1].toISOString().split('T')[0]
          };
        }
      }
      // TODO: Revisit this logic if we want a different default min date
      return { min: '1975-01-01', max: new Date().toISOString().split('T')[0] };
    };
  
    const { min, max } = getMinMaxDates();
  
    return (
      <div className="filter-section date-filter-section">
        <h4>Date Range</h4>
        <div className="date-filter-controls">
          <div className="date-filter-row">
            <label>
              Start Date:
              <input
                type="date"
                value={dateFilters.startDate || ''}
                min={min}
                max={max}
                onChange={(e) => handleDateFilterChange('startDate', e.target.value || null)}
                className="date-input"
              />
            </label>
          </div>
          <div className="date-filter-row">
            <label>
              End Date:
              <input
                type="date"
                value={dateFilters.endDate || ''}
                min={min}
                max={max}
                onChange={(e) => handleDateFilterChange('endDate', e.target.value || null)}
                className="date-input"
              />
            </label>
          </div>
          <button 
            className="clear-date-filters" 
            onClick={() => setDateFilters({ startDate: null, endDate: null })}
          >
            Clear Date Filters
          </button>
        </div>
      </div>
    )
  };

  // CSS classes for floating action button and toolbar animation
  const floatingButtonClass = `floating-action-button ${showRankingToolbar ? 'rotated' : ''}`;
  const rankingToolbarClass = `ranking-toolbar-container ${showRankingToolbar ? '' : 'hidden'}`;

  return (
    <div className="parameter-tracking-container">
      {!previewMode && (<p>Track how scientific consensus around environmental parameters has changed over time.</p>)}
      
      {isLoading ? (
        <div className="loading-indicator">
          <div className="spinner"></div>
          <p>Loading parameter data...</p>
        </div>
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : (
        <>
          <div className="parameter-dashboard">            
            {!previewMode && (<div className="filters-panel">
              <h3>Filters</h3>
              
              {/* Add the date filter section first */}
              <DateFilterSection />
              
              {/* Render all available filter sections dynamically */}
              {Object.keys(availableFilters).map(filterType => 
                renderFilterSection(filterType, availableFilters[filterType])
              )}
            </div>)}
            
            <div className="chart-panel">
              <h3>
                {parameterName} for {stockName} Parameter Values Over Time
                {parameterData && parameterData.standard_unit && parameterData.standard_unit.toLowerCase() !== 'm' ? ` ${getStandardUnit()}` : ''}
              </h3>
              {chartData.length > 0 ? (
                <div 
                  className="chart-container" 
                  ref={containerRef}
                >
                  <svg
                    ref={svgRef}
                  ></svg>
                </div>

              ) : (
                <p className="no-data-message">
                  No data available for the selected parameter and filters.
                </p>
              )}
            </div>
          </div>

          {!previewMode && (<div className="data-table-section">
            <h3>Data Points</h3>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Publication Date</th>
                  <th>Value</th>
                  <th>Location</th>
                  {/* NEW - Add ranking score column */}
                  <th>Ranking Score</th>
                  {/* Dynamically add columns for each filter type */}
                  {Object.keys(availableFilters)
                    .filter(field => !['country', 'region'].includes(field)) // Skip location fields already shown
                    .map(field => (
                    <th key={field}>{field.charAt(0).toUpperCase() + field.slice(1)}</th>
                  ))}
                  <th>Paper Title</th>
                </tr>
              </thead>
              <tbody>
                {chartData.map((point, index) => (
                  <tr key={index}>
                    <td>{point.date}</td>
                    <td>{point.tooltip.raw_value || point.value}</td>
                    <td>{point.tooltip.location}</td>
                    {/* NEW - Show ranking score */}
                    <td>
                      {point.paperId && paperRankings[point.paperId] 
                        ? `${(paperRankings[point.paperId] * 100).toFixed(0)}%` 
                        : '-'}
                    </td>
                    {/* Dynamically add cells for each filter type */}
                    {Object.keys(availableFilters)
                      .filter(field => !['country', 'region'].includes(field))
                      .map(field => (
                      <td key={field}>{point.tooltip[field] || '-'}</td>
                    ))}
                    <td>{point.tooltip.paperTitle}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>)}
        </>
      )}

      {/* NEW - Floating action button for ranking tool */}
      {!previewMode && (<>
        <button 
        className={floatingButtonClass}
        onClick={() => {
          console.log(`${showRankingToolbar ? 'Hiding' : 'Showing'} ranking toolbar`);
          setShowRankingToolbar(!showRankingToolbar);
        }}
        aria-label="Toggle ranking tool"
        title="Paper ranking tool"
      >
        {/* Icon similar to sliders/scales */}
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="4" y1="21" x2="4" y2="14"></line>
          <line x1="4" y1="10" x2="4" y2="3"></line>
          <line x1="12" y1="21" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12" y2="3"></line>
          <line x1="20" y1="21" x2="20" y2="16"></line>
          <line x1="20" y1="12" x2="20" y2="3"></line>
          <line x1="1" y1="14" x2="7" y2="14"></line>
          <line x1="9" y1="8" x2="15" y2="8"></line>
          <line x1="17" y1="16" x2="23" y2="16"></line>
        </svg>
      </button>

      <div className={rankingToolbarClass}>
        {/* Add a close button at the top right of the toolbar */}
        <button
          onClick={() => setShowRankingToolbar(false)}
          className="ranking-toolbar-close"
        >
          ✕
        </button>

        <h3 className="ranking-toolbar-title">
          Paper Ranking Tool
        </h3>

        <p className="ranking-toolbar-description">
          Adjust the weights below to change how data points are sized in the chart. 
          Higher weights give more importance to that factor in determining point size.
        </p>

        {/* Show loading indicator while fetching rankings */}
        {isRankingLoading && (
          <div className="ranking-loading">
            <div className="ranking-loading-spinner"></div>
            <span className="ranking-loading-text">Updating rankings...</span>
          </div>
        )}

        {/* Embed the PaperRankingToolbar component */}
        {/* <PaperRankingToolbar 
          weights={rankingWeights} 
          onWeightsChange={handleWeightsChange}
        /> */}
      </div>
      </>)}
    </div>
  );
};

export default ParameterTracking;